<script setup lang="ts">
    import { inject, ref } from 'vue';
    import List from '@shared/components/List/List.vue';
    import InfoPopoverButton from '@shared/components/Form/InfoPopoverButton.vue';
    import Preview from '@shared/components/Preview.vue';
    import StatisticsSourcesItem from './StatisticsSourcesItem.vue';
    import type { SurveyStatisticsSource } from '../../SurveysTypes.d';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { combineUrlSegments } from '@shared/utils/combineUrlSegments';

    const props = defineProps<{
        sources: SurveyStatisticsSource[];
    }>();

    const emit = defineEmits<{
        (e: 'show-non-respondents'): void;
        (e: 'show-respondents-from-issue', source: SurveyStatisticsSource): void;
        (e: 'show-non-respondents-from-issue', source: SurveyStatisticsSource): void;
    }>();

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    const previewUrl = ref<string | null>(null);

    const showPreview = (issueId: string) => {
        previewUrl.value = combineUrlSegments(contextAccess.state.currentAccount!.PublicEventUrl.PublicHost, 'Issues', '' + issueId, '?preview=true');
    };

    const emptyIssueId = '00000000-0000-0000-0000-000000000000';

    const issueSources = props.sources.filter((source) => source.IssueId !== emptyIssueId);

    const orderOptions = [
        { display: $Resources.IssuesSortBySubjectLabel, key: 'Subject' },
        { display: $Resources.SurveyResponseCountLabel, key: 'ResponseCount' },
        { display: $Resources.SurveyRespondentCountLabel, key: 'RespondentCount' },
    ];
</script>

<template>
    <List
        :items="issueSources"
        itemId="IssueId"
        :perPage="issueSources.length"
        :title="$Resources.StatisticsSurveySourcesHeading"
        :description="$Resources.StatisticsSurveySourcesIntroduction"
        :filterKeys="['Subject', 'IssueName']"
        :orderOptions="orderOptions">
        <template #toolbar>
            <button type="button" class="btn btn-primary" @click="emit('show-non-respondents')">
                {{ $Resources.StatisticsSurveyNonRespondentsButton }}
            </button>
        </template>

        <template #item="{ item }: { item: SurveyStatisticsSource }">
            <StatisticsSourcesItem
                :source="item"
                @show-preview="showPreview(item.IssueId)"
                @show-respondents="emit('show-respondents-from-issue', item)"
                @show-non-respondents="emit('show-non-respondents-from-issue', item)" />
        </template>
    </List>

    <Preview :previewUrl="previewUrl" @close="previewUrl = null" />
</template>
